import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Paperclip, Send, Smile, X, FileAudio, FileVideo, FileText, Image as ImageIcon } from 'lucide-react'
import EmojiPicker from 'emoji-picker-react'
import { Button } from '@/clientWeb/components/ui/button'
import { Input } from '@/clientWeb/components/ui/input'
import { toast } from '@/clientWeb/components/ui/toast-utils'
import { checkFileForUpload } from '@/lib/upload-policy'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { addInputFocusHandler } from '@vc/utils/inputFocusHelper'
import { ChatDisabledNotice } from './ChatDisabledNotice'

/**
 * The chat footer carries `contain: layout style paint`, which clips
 * absolutely-positioned children. We portal the attach menu and the emoji
 * picker to `document.body` and position them with `fixed` coords derived
 * from the trigger's `getBoundingClientRect()` so they're free to render
 * above the input on top of everything else.
 */
function useAnchorRect(open: boolean, ref: React.RefObject<HTMLElement | null>): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useEffect(() => {
    if (!open || !ref.current) {
      setRect(null)
      return
    }
    const measure = () => ref.current && setRect(ref.current.getBoundingClientRect())
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, ref])
  return rect
}

/** Attachment kind picked by the user. The parent decides what to do with
 *  the resulting File (upload, render in the message list as an audio/video
 *  player or doc card, etc.). */
export type ChatAttachmentKind = 'image' | 'audio' | 'video' | 'document'

interface ChatComposerProps {
  text: string
  onTextChange: (text: string) => void
  canChat: boolean
  isBroadcast: boolean
  /** Picks which "the host turned this off" message the disabled state shows. */
  isPrivate?: boolean
  onSend: () => void
  /** Fired when the user picks a file from the attachment popover. */
  onAttach?: (file: File, kind: ChatAttachmentKind) => void
  /** When false, hide the attach popover trigger (host disabled file sharing). */
  canAttachFiles?: boolean
  /** When false, hide the emoji picker trigger (host disabled emoji reactions). */
  canUseEmojis?: boolean
}

const ATTACHMENT_ACCEPT: Record<ChatAttachmentKind, string> = {
  image: 'image/*',
  audio: 'audio/*',
  video: 'video/*',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
}

const NOT_ACCEPTED_KEY: Record<ChatAttachmentKind, string> = {
  image: 'panels.notAPhoto',
  audio: 'panels.notAnAudioFile',
  video: 'panels.notAVideo',
  document: 'panels.notASupportedDocument',
}

/**
 * Whether `file` passes the picker's own `accept` filter.
 *
 * `accept` only pre-filters the OS dialog, which still offers "All files". A
 * .pptx picked through Audio went out as an audio message, and every
 * participant got an audio player that could not play it. The kind decides
 * how the message renders, so the file has to really be that kind.
 */
function matchesAccept(file: File, accept: string): boolean {
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return accept.split(',').some((entry) => {
    const rule = entry.trim().toLowerCase()
    if (rule.startsWith('.')) return name.endsWith(rule)
    if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1))
    return type === rule
  })
}

export function ChatComposer({
  text,
  onTextChange,
  canChat,
  isBroadcast,
  isPrivate = false,
  onSend,
  onAttach,
  canAttachFiles = true,
  canUseEmojis = true,
}: ChatComposerProps) {
  const t = useTranslation()
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null)
  const emojiPickerRef = useRef<HTMLDivElement | null>(null)
  const attachButtonRef = useRef<HTMLButtonElement | null>(null)
  const attachMenuRef = useRef<HTMLDivElement | null>(null)
  // One hidden input per kind so the `accept` filter is honoured by the OS
  // file dialog (a single input would surface every type at once).
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const audioInputRef = useRef<HTMLInputElement | null>(null)
  const videoInputRef = useRef<HTMLInputElement | null>(null)
  const documentInputRef = useRef<HTMLInputElement | null>(null)
  const inputContainerRef = useRef<HTMLDivElement | null>(null)

  const refFor = (kind: ChatAttachmentKind) => {
    switch (kind) {
      case 'image': return imageInputRef
      case 'audio': return audioInputRef
      case 'video': return videoInputRef
      case 'document': return documentInputRef
    }
  }

  const handlePickKind = (kind: ChatAttachmentKind) => {
    setShowAttachMenu(false)
    refFor(kind).current?.click()
  }

  const handleFileChange = (kind: ChatAttachmentKind) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      // Reset the value so the same file can be picked again later.
      e.target.value = ''
      if (!file) return
      // Media kinds must really be that kind, since the kind picks the player.
      // Documents are decided by the server's policy alone: its list is wider
      // than this picker's filter and is the one the upload is held to.
      if (kind !== 'document' && !matchesAccept(file, ATTACHMENT_ACCEPT[kind])) {
        toast.error({
          title: t('panels.fileNotSupported'),
          description: t(NOT_ACCEPTED_KEY[kind], { name: file.name }),
        })
        return
      }
      const check = await checkFileForUpload(file, 'attachment')
      if (!check.ok) {
        toast.error({ title: t('panels.fileNotSupported'), description: check.message })
        return
      }
      onAttach?.(file, kind)
    }

  // Anchor rects (in viewport coords) so the portalled popovers can be
  // pinned just above their triggers.
  const attachRect = useAnchorRect(showAttachMenu, attachButtonRef)
  const emojiRect = useAnchorRect(showEmojiPicker, emojiButtonRef)

  useEffect(() => {
    const inputEl = inputContainerRef.current?.querySelector('input')
    if (!inputEl) return
    return addInputFocusHandler(inputEl, {
      delay: 150,
      block: 'center',
      immediate: true,
    })
  }, [])

  useEffect(() => {
    if (!showEmojiPicker) return
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  useEffect(() => {
    if (!showAttachMenu) return
    const handleClickOutside = (event: MouseEvent) => {
      if (
        attachMenuRef.current &&
        !attachMenuRef.current.contains(event.target as Node) &&
        attachButtonRef.current &&
        !attachButtonRef.current.contains(event.target as Node)
      ) {
        setShowAttachMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showAttachMenu])

  if (!canChat) return <ChatDisabledNotice isPrivate={isPrivate} />

  return (
    <div className="mobile-sheet-footer border-t border-vc-panel-border px-4 py-4 bg-panelcolor flex-shrink-0">
      <div className="flex items-center gap-2 relative">
        {canAttachFiles && (
          <button
            ref={attachButtonRef}
            type="button"
            onClick={() => setShowAttachMenu((v) => !v)}
            className="group w-11 h-11 cursor-pointer rounded-full flex justify-center items-center bg-primary-100/30 hover:bg-primary-100/20 dark:bg-muted dark:hover:bg-muted/70 transition shrink-0"
            aria-label={t('panels.attach')}
            aria-expanded={showAttachMenu}
          >
            <Paperclip className="w-4 h-4 text-slate-600 dark:text-muted-foreground" />
          </button>
        )}

        {/* Attachment kind picker is rendered via portal below — see the
            <createPortal> block near the end of this component. */}

        {/* One hidden input per kind so the `accept` filter sticks. */}
        <input
          ref={imageInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT.image}
          onChange={handleFileChange('image')}
          className="hidden"
        />
        <input
          ref={audioInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT.audio}
          onChange={handleFileChange('audio')}
          className="hidden"
        />
        <input
          ref={videoInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT.video}
          onChange={handleFileChange('video')}
          className="hidden"
        />
        <input
          ref={documentInputRef}
          type="file"
          accept={ATTACHMENT_ACCEPT.document}
          onChange={handleFileChange('document')}
          className="hidden"
        />
        <div className="flex-1 relative" ref={inputContainerRef}>
          <Input
            value={text}
            onChange={(e) => onTextChange(e.target.value.slice(0, 2000))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (text.trim() && text.length <= 2000) onSend()
              }
            }}
            placeholder={isBroadcast ? t('panels.typeBroadcastMessage') : t('panels.typeSomething')}
            inputMode="text"
            maxLength={2000}
            className="w-full rounded-full bg-slate-100 dark:bg-muted px-4 py-2.5 text-slate-800 dark:text-foreground text-sm placeholder:text-slate-400 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition"
          />
          {text.length > 1800 && (
            <span className={`absolute -top-5 right-2 text-[10px] ${text.length >= 2000 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {text.length}/2000
            </span>
          )}
        </div>
        {canUseEmojis && (
          <button
            ref={emojiButtonRef}
            type="button"
            onClick={() => setShowEmojiPicker((v) => !v)}
            className="grid cursor-pointer h-10 w-10 place-items-center rounded-full dark:text-foreground text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition relative"
          >
            {showEmojiPicker ? <X className="h-5 w-5" /> : <Smile className="h-5 w-5 text-slate-600 dark:text-muted-foreground" />}
          </button>
        )}

        {/* Emoji picker is rendered via portal below — see the
            <createPortal> block near the end of this component. */}

        <Button
          variant="default"
          onClick={onSend}
          disabled={!text.trim()}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>

      {/* ── Portalled popovers ────────────────────────────────────────────
          Both popovers escape the footer's `contain: layout style paint`
          by rendering into document.body with fixed positioning anchored
          to their trigger buttons. */}
      {showAttachMenu && attachRect && typeof document !== 'undefined' && createPortal(
        <div
          ref={attachMenuRef}
          className="fixed z-[9999] bg-card dark:bg-card border border-stroke-100 dark:border-border rounded-2xl shadow-xl overflow-hidden min-w-[180px]"
          style={{
            // Anchor above the button; `-8` is the visual gap.
            top: Math.max(8, attachRect.top - 8 - 200),
            left: attachRect.left,
          }}
        >
          <ul className="py-1">
            {([
              { kind: 'image' as const, icon: ImageIcon, label: t('panels.image'), tint: 'text-violet-500 bg-violet-500/10' },
              { kind: 'video' as const, icon: FileVideo, label: t('panels.video'), tint: 'text-rose-500 bg-rose-500/10' },
              { kind: 'audio' as const, icon: FileAudio, label: t('panels.audio'), tint: 'text-amber-500 bg-amber-500/10' },
              { kind: 'document' as const, icon: FileText, label: t('panels.document'), tint: 'text-sky-500 bg-sky-500/10' },
            ]).map(({ kind, icon: Icon, label, tint }) => (
              <li key={kind}>
                <button
                  type="button"
                  onClick={() => handlePickKind(kind)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tint}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="truncate">{label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}

      {showEmojiPicker && emojiRect && typeof document !== 'undefined' && createPortal(
        <div
          ref={emojiPickerRef}
          className="fixed z-[9999]"
          style={{
            top: Math.max(8, emojiRect.top - 8 - 400),
            // Right-align with the emoji button so the picker doesn't
            // overflow the screen edge.
            left: Math.max(8, emojiRect.right - 300),
          }}
        >
          <EmojiPicker
            onEmojiClick={(emojiData) => onTextChange(text + emojiData.emoji)}
            autoFocusSearch={false}
            theme={(document.documentElement.classList.contains('dark') ? 'dark' : 'light') as Parameters<typeof EmojiPicker>[0]['theme']}
            width={300}
            height={400}
          />
        </div>,
        document.body,
      )}
    </div>
  )
}
